// Dependency-free: also used before the app/Sentry chunk has loaded. Never
// fetch, retain arbitrary URLs, or let diagnostics affect recovery.
const RELOAD_ATTEMPT_KEY = "jungle_asset_reload_attempt";
const MAX_RELOAD_ATTEMPT_AGE_MS = 5 * 60_000;
const DEPLOYMENT_ID_PATTERN = /^dpl_[A-Za-z0-9]{1,80}$/;
const BUILD_ID_PATTERN = /^[A-Za-z0-9._+-]{1,128}$/;
const ASSET_FILE_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9_.-]{0,119}\.(?:js|css)$/;

export type AssetRecoveryDecision =
  | "not_observed" | "first_failure" | "build_already_attempted"
  | "probe_pending" | "deployment_changed" | "deployment_unchanged"
  | "deployment_unknown" | "transition_already_attempted" | "probe_failed"
  | "storage_unavailable" | "local_fallback";

export function getAssetDeploymentId(): string {
  const deploymentId = import.meta.env.VITE_DEPLOYMENT_ID;
  // Unprotected builds may use the configured Git SHA or CI build identity.
  // Only asset URL pinning requires Vercel's stricter provider ID format.
  return typeof deploymentId === "string" && BUILD_ID_PATTERN.test(deploymentId) ? deploymentId : "unknown";
}

function getFailedAsset(error: unknown): { url: URL; file: string; deploymentId: string | null } | null {
  if (!(error instanceof Error)) return null;
  const assetUrl = error.message.match(/(?:https?:\/\/[^\s'"<>]+)?\/assets\/[^\s'"<>]+/)?.[0];
  if (!assetUrl) return null;
  try {
    const url = new URL(assetUrl, window.location.origin);
    const file = url.pathname.slice("/assets/".length);
    const isSameOrigin = url.origin === window.location.origin;
    const isBuiltAsset = url.pathname.startsWith("/assets/") && ASSET_FILE_PATTERN.test(file);
    if (!isSameOrigin || !isBuiltAsset) return null;
    const deploymentId = url.searchParams.get("dpl");
    return { url, file, deploymentId: deploymentId && DEPLOYMENT_ID_PATTERN.test(deploymentId) ? deploymentId : null };
  } catch {
    return null;
  }
}

function finiteNonnegative(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
}

export function getAssetLoadDiagnostics(error: unknown) {
  const diagnostic = {
    schema_version: 1,
    deployment_id: getAssetDeploymentId(),
    asset_file: null as string | null,
    asset_deployment_id: null as string | null,
    online: null as boolean | null,
    visibility_state: "unknown",
    document_age_ms: null as number | null,
    resource_timing: "unavailable",
    resource_response_status: null as number | null,
    resource_duration_ms: null as number | null,
    resource_transfer_bytes: null as number | null,
  };
  try {
    const asset = getFailedAsset(error);
    diagnostic.asset_file = asset?.file ?? null;
    diagnostic.asset_deployment_id = asset?.deploymentId ?? null;
    diagnostic.online = typeof navigator.onLine === "boolean" ? navigator.onLine : null;
    diagnostic.visibility_state = document.visibilityState;
    diagnostic.document_age_ms = finiteNonnegative(performance.now());
    if (asset) {
      const resources = performance.getEntriesByName(asset.url.href, "resource");
      const resource = resources[resources.length - 1] as PerformanceResourceTiming & { responseStatus?: number } | undefined;
      if (resource) {
        const responseStatus = finiteNonnegative(resource.responseStatus);
        diagnostic.resource_timing = "available";
        // Status 0 and zero sizes are ambiguous (cache, failed request, or
        // browser restrictions). Never infer an HTTP 404 or network loss.
        diagnostic.resource_response_status = responseStatus && responseStatus >= 100 && responseStatus <= 599 ? responseStatus : null;
        diagnostic.resource_duration_ms = finiteNonnegative(resource.duration);
        diagnostic.resource_transfer_bytes = finiteNonnegative(resource.transferSize);
      }
    }
  } catch {
    // Even restricted/unsupported browser APIs must not break the boundary.
  }
  return diagnostic;
}

export function rememberAssetReloadAttempt(error: unknown, decision: AssetRecoveryDecision): void {
  try {
    const diagnostic = getAssetLoadDiagnostics(error);
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, JSON.stringify({
      attempted_at: Date.now(),
      deployment_id: diagnostic.deployment_id,
      asset_file: diagnostic.asset_file,
      asset_deployment_id: diagnostic.asset_deployment_id,
      online: diagnostic.online,
      decision,
    }));
  } catch {
    // Storage is optional; the existing reload-loop guard remains authoritative.
  }
}

/** A consumed marker proves only that the next app runtime loaded, not that
 * the route mounted or that the learner's interrupted journey recovered. */
export function consumeAssetReloadAttempt() {
  try {
    const storedAttempt = window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
    window.sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
    if (!storedAttempt || storedAttempt.length > 1_000) return null;
    const attempt = JSON.parse(storedAttempt);
    if (!attempt || typeof attempt !== "object") return null;
    const hasValidAttemptTime = typeof attempt.attempted_at === "number" && Number.isFinite(attempt.attempted_at);
    if (!hasValidAttemptTime) return null;
    const age = Date.now() - attempt.attempted_at;
    // A stale marker or a clock moving backwards cannot qualify this runtime.
    const isRecentAttempt = age >= 0 && age <= MAX_RELOAD_ATTEMPT_AGE_MS;
    if (!isRecentAttempt) return null;
    if (attempt.decision !== "first_failure" && attempt.decision !== "deployment_changed") return null;
    // Project only known bounded fields; never forward arbitrary stored data.
    return {
      attempted_at: attempt.attempted_at as number,
      deployment_id: typeof attempt.deployment_id === "string" && BUILD_ID_PATTERN.test(attempt.deployment_id) ? attempt.deployment_id : "unknown",
      asset_file: typeof attempt.asset_file === "string" && ASSET_FILE_PATTERN.test(attempt.asset_file) ? attempt.asset_file : null,
      asset_deployment_id: typeof attempt.asset_deployment_id === "string" && DEPLOYMENT_ID_PATTERN.test(attempt.asset_deployment_id) ? attempt.asset_deployment_id : null,
      online: typeof attempt.online === "boolean" ? attempt.online : null,
      decision: attempt.decision as "first_failure" | "deployment_changed",
      elapsed_ms: age,
    };
  } catch {
    return null;
  }
}
